/**
 * c5Flag — feature-flag for the C5 "Indigo & Ink" DOM frame (Layer 0).
 *
 * Mirrors the `paintedWorldFlag` pattern. Resolution order:
 *   1. URL query string `?c5=1` (or `=true` / `=0` / `=false`)
 *   2. localStorage key `rpg-gibli:c5-ui-flag`
 *   3. Vite env var `VITE_C5_UI` (build-time default)
 *   4. Fallback: ON everywhere (shipped 2026-08-16; `?c5=0` is the escape hatch).
 *
 * See docs/design/ui-c5-indigo-ink.md.
 */

const STORAGE_KEY = 'rpg-gibli:c5-ui-flag';
const QUERY_KEY = 'c5';

interface ImportMetaEnvLike {
  env?: { VITE_C5_UI?: string; DEV?: boolean };
}

/** Parse a flag-ish string. Returns null when the value carries no signal. */
export function parseFlagValue(raw: string | null | undefined): boolean | null {
  if (raw === null || raw === undefined) return null;
  const v = raw.trim().toLowerCase();
  if (v === '') return true; // bare `?c5` means on
  if (v === '1' || v === 'true' || v === 'on' || v === 'yes') return true;
  if (v === '0' || v === 'false' || v === 'off' || v === 'no') return false;
  return null;
}

export interface C5FlagSources {
  query?: string | null;
  storage?: string | null;
  env?: string | null;
  /** Retained for source plumbing/telemetry; no longer affects the default. */
  dev?: boolean;
}

/**
 * Pure resolution of the flag from its four sources. Exported for tests;
 * `isC5UiEnabled()` is the runtime entry point.
 */
export function resolveC5Flag(sources: C5FlagSources): boolean {
  const q = parseFlagValue(sources.query);
  if (q !== null) return q;
  const s = parseFlagValue(sources.storage);
  if (s !== null) return s;
  const e = parseFlagValue(sources.env);
  if (e !== null) return e;
  return true;
}

function readQueryRaw(): string | null {
  try {
    const loc = (globalThis as unknown as { location?: { search?: string } }).location;
    if (!loc?.search) return null;
    const params = new URLSearchParams(loc.search);
    if (!params.has(QUERY_KEY)) return null;
    return params.get(QUERY_KEY) ?? '';
  } catch {
    return null;
  }
}

function readStorageRaw(): string | null {
  try {
    const ls = (globalThis as unknown as { localStorage?: Storage }).localStorage;
    return ls?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function readEnv(): { env: string | null; dev: boolean } {
  try {
    const meta = (import.meta as unknown as ImportMetaEnvLike).env;
    return { env: meta?.VITE_C5_UI ?? null, dev: meta?.DEV === true };
  } catch {
    return { env: null, dev: false };
  }
}

/** True when the C5 DOM frame should be mounted. */
export function isC5UiEnabled(): boolean {
  const { env, dev } = readEnv();
  return resolveC5Flag({
    query: readQueryRaw(),
    storage: readStorageRaw(),
    env,
    dev,
  });
}

/** Persist a manual override (dev tooling / settings panel). */
export function setC5UiFlag(value: boolean): void {
  try {
    const ls = (globalThis as unknown as { localStorage?: Storage }).localStorage;
    ls?.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch {
    // best-effort
  }
}

export const C5_UI_STORAGE_KEY = STORAGE_KEY;
export const C5_UI_QUERY_KEY = QUERY_KEY;
